import { useEffect, useState, useContext } from 'react';
import { FormControlLabel, Switch } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Box from './Box';
import Task from '../components/Task';
import EmptyState from '../components/EmptyState';
import UnifiedModal from '../components/UnifiedModal';
import { Context } from '../../../AuthContext';
import { useCategory } from '../../../CategoryContext';
import { toast } from 'react-toastify';
import SortByMenu from '../../../components/SortByMenu/SortByMenu';
import { getWorkbenchSortOptions } from '../../../utils/workbenchSortOptions';
import '../Workbench.css';

const label = { inputProps: { 'aria-label': 'Switch' } };

const Tasks = ({ isVertical = false, verticalId }) => {
  const { user } = useContext(Context);
  const { verticals } = useCategory();
  const [tasks, setTasks] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [overdueTasksCounter, setOverdueTasksCounter] = useState(0);
  const [recentlyArchivedIds, setRecentlyArchivedIds] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // View-only filter (intentionally not persisted): vertical task manager only.
  const [assignedToMeOnly, setAssignedToMeOnly] = useState(false);

  useEffect(() => {
    const fetchTasks = async () => {
      if (!user?.uid) return;
      setIsLoading(true);
      try {
        const token = await user.getIdToken();
        const url =
          process.env.REACT_APP_FLASK_WEBSERVER +
          'get_tasks' +
          (isVertical && verticalId ? `?vertical_id=${encodeURIComponent(verticalId)}` : '');
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setTasks(data.data);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTasks();
  }, [user?.uid, isVertical, verticalId]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const overdueTasks = tasks.filter((t) => {
      if (!t.deadline) return false;
      const taskDate = new Date(t.deadline);
      if (isNaN(taskDate.getTime())) return false;
      taskDate.setHours(0, 0, 0, 0);
      return taskDate < today && !t.isCompleted;
    });

    setOverdueTasksCounter(overdueTasks.length);
  }, [tasks]);

  const handleDelete = async (id) => {
    setTasks(tasks.filter((el) => el._id !== id));
    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}delete_task/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.log('Failed to delete task:', error);
    }
  };

  const handleCopyToPersonal = async (task) => {
    try {
      if (!user) return;
      const token = await user.getIdToken();
      const payload = {
        title: task.title,
        details: task.details,
        deadline: task.deadline,
        isCompleted: false,
        associatedVerticalId: verticalId,
        tagColor: '#356AC3',
      };
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}add_task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Failed to copy task to personal workbench:', error);
    }
  };

  const handleOpenModal = (item = null) => {
    setEditItem(item);
    setOpenModal(true);
  };

  const handleSave = (updatedItem) => {
    setTasks((prev) =>
      editItem
        ? prev.map((item) => (item._id === editItem._id ? updatedItem : item))
        : [updatedItem, ...prev]
    );
    setOpenModal(false);
  };

  const toggleHandler = async (id) => {
    const itemToUpdate = tasks.find((item) => item._id === id);
    const isCurrentlyCompleted = itemToUpdate.isCompleted;

    setTasks(
      tasks.map((item) =>
        item._id === id ? { ...item, isCompleted: !isCurrentlyCompleted } : item
      )
    );

    if (!isCurrentlyCompleted) {
      setRecentlyArchivedIds((prev) => [...prev, id]);
      setTimeout(() => {
        setRecentlyArchivedIds((prev) => prev.filter((itemId) => itemId !== id));
      }, 3000);
    }

    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}edit_task?id=${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isCompleted: !isCurrentlyCompleted }),
      });
    } catch (error) {
      console.error('Failed to toggle task:', error);
    }
  };

  // The current user's name, as used for task assignment (display name, email fallback).
  const myName = (user?.displayName || user?.email || '').trim().toLowerCase();

  // Whether a task is currently shown, combining the archived and (vertical-only)
  // "assigned only to me" filters. Shared by the rendered list and drag handling so
  // their indices stay in sync.
  const isVisibleTask = (task) => {
    const archiveOk =
      showArchived ? true : !task.isCompleted || recentlyArchivedIds.includes(task._id);
    const assigneeOk =
      !(isVertical && assignedToMeOnly) ||
      (myName !== '' && (task.assignedTo || '').trim().toLowerCase() === myName);
    return archiveOk && assigneeOk;
  };

  const handleDragEnd = async (result) => {
    if (!canManage) return;
    if (!result.destination) return;

    const sourceItem = tasks.filter(isVisibleTask)[result.source.index];
    const destinationItem = tasks.filter(isVisibleTask)[result.destination.index];

    const updated = Array.from(tasks);
    const sourceIdx = updated.findIndex((item) => item._id === sourceItem._id);
    const destIdx = updated.findIndex((item) => item._id === destinationItem._id);

    const [moved] = updated.splice(sourceIdx, 1);
    updated.splice(destIdx, 0, moved);

    setTasks(updated);

    try {
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}update_order_dashboard?collection=tasks`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updated.map((item) => ({ id: item._id }))),
        }
      );
    } catch (error) {
      console.error('Failed to update tasks order:', error);
    }
  };

  // Owner/editor-only management rights. The personal workbench is always the
  // user's own, so it's always allowed there; the vertical workbench requires
  // edit rights. Members (no edit rights) cannot sort, and the per-item menu
  // (the only way to edit/delete) is hidden from them.
  const canManage =
    !isVertical ||
    (verticals || []).some(
      (v) =>
        v.id === verticalId &&
        (v.status === 'Created' || /^(editor|advancededitor)$/i.test(v.permission || ''))
    );
  const canSort = canManage;
  const sortOptions = getWorkbenchSortOptions({ isVertical, type: 'task', verticals });

  // Apply a chosen sort, then persist the new order the same way a drag does.
  const handleSort = async (option) => {
    const sorted = [...tasks].sort(option.comparator);
    setTasks(sorted);
    try {
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_FLASK_WEBSERVER}update_order_dashboard?collection=tasks`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(sorted.map((item) => ({ id: item._id }))),
        }
      );
      if (res.ok) toast.success(`Sorted by ${option.label}`, { className: 'toast', autoClose: 4000 });
      else toast.error('Unable to save sort order', { className: 'toast', autoClose: 4000 });
    } catch (error) {
      console.error('Failed to sort tasks:', error);
      toast.error('Unable to save sort order', { className: 'toast', autoClose: 4000 });
    }
  };

  const filteredTasks = tasks.filter(isVisibleTask);

  const count = filteredTasks.length;

  const besideAddIconText =
    overdueTasksCounter > 0 ? (
      <span style={{ color: 'red', fontWeight: 500, fontSize: '12px' }}>
        ⚠ {overdueTasksCounter} overdue
      </span>
    ) : null;

  return (
    <div className="workbench-tasks">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '0 16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {canSort && sortOptions.length > 0 && tasks.length > 1 && (
            <SortByMenu sections={[{ options: sortOptions }]} onSelect={handleSort} />
          )}
          {isVertical && (
            <FormControlLabel
              control={
                <Switch
                  {...label}
                  checked={assignedToMeOnly}
                  onChange={() => setAssignedToMeOnly((prev) => !prev)}
                />
              }
              label="ASSIGNED ONLY TO ME"
              labelPlacement="start"
            />
          )}
        </div>
        <FormControlLabel
          control={
            <Switch
              {...label}
              checked={showArchived}
              onChange={() => setShowArchived((prev) => !prev)}
            />
          }
          label="SHOW ARCHIVED"
          labelPlacement="start"
        />
      </div>

      <UnifiedModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        item={editItem}
        type="task"
        onSave={handleSave}
        isVertical={isVertical}
        verticalId={verticalId}
      />

      <Box
        count={count}
        onAddHandler={() => handleOpenModal()}
        title="Tasks & Deadlines"
        icon="task.svg"
        besideAddIconText={besideAddIconText}
        isLoading={isLoading}
        canAdd={canManage}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tasks">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={{ height: '100%' }}>
                {filteredTasks.length > 0 ? (
                  filteredTasks.map((task, index) => (
                    <Draggable
                      key={task._id}
                      draggableId={task._id}
                      index={index}
                      isDragDisabled={!canManage}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <Task
                            id={task._id}
                            deleteHandler={() => handleDelete(task._id)}
                            editHandler={() => handleOpenModal(task)}
                            toggleHandler={() => toggleHandler(task._id)}
                            title={task.title}
                            deadline={task.deadline}
                            details={task.details}
                            isCompleted={task.isCompleted}
                            associatedVerticalId={isVertical ? null : task.associatedVerticalId}
                            tagColor={isVertical ? null : task.tagColor}
                            isVertical={isVertical}
                            assignedTo={task.assignedTo}
                            assignedBy={task.assignedBy}
                            createdAt={task.created_at}
                            copyToPersonalHandler={
                              isVertical &&
                              myName !== '' &&
                              (task.assignedTo || '').trim().toLowerCase() === myName
                                ? () => handleCopyToPersonal(task)
                                : undefined
                            }
                            canManage={canManage}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <EmptyState
                    imageSrc="/task.svg"
                    type="task"
                    onAddClick={canManage ? () => handleOpenModal() : undefined}
                  />
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </div>
  );
};

export default Tasks;
